import { Component, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import {
  NotificationBackendService,
  NotificationTarget,
  NotificationStatus
} from '../../services/notification-backend.service';
import { NotificationService } from '../../services/notification.service';
import { PERMISSIONS, splitPermission, userHasAnyPermission } from '../../services/permissions';

interface PublisherOption { ID: string; Name: string; }
interface UserOption { ID: string; email: string; entityName?: string; dataAccessLevel?: string; permissions?: string[]; }

// Un elemento foglia dell'albero-filtro: `value` è il permesso completo in
// dot-notation (es. 'reach.audience'), `leaf` l'ultimo segmento usato per l'etichetta.
interface PermFilterChild { value: string; leaf: string; }
// Un gruppo di primo livello dell'albero-filtro (un'area). L'area stessa è
// selezionabile (`value` = '<area>.all'); gli eventuali sotto-permessi sono
// mostrati come secondo livello in `children`.
interface PermFilterGroup { area: string; value: string; children: PermFilterChild[]; }

/**
 * Costruisce dinamicamente l'albero dei filtri a partire dall'elenco reale dei
 * permessi (enum UserPermissions). Nuovi permessi aggiunti all'enum compaiono
 * automaticamente. Il permesso globale 'all' è gestito a parte (voce in cima).
 */
function buildPermFilterGroups(): PermFilterGroup[] {
  const byArea = new Map<string, string[]>();
  for (const perm of PERMISSIONS) {
    const parts = splitPermission(perm);
    if (parts[0] === 'all') continue;
    if (!byArea.has(parts[0])) byArea.set(parts[0], []);
    byArea.get(parts[0])!.push(perm);
  }
  const groups: PermFilterGroup[] = [];
  for (const [area, perms] of byArea) {
    const children = perms
      .filter(p => splitPermission(p)[1] !== 'all')
      .map(p => ({ value: p, leaf: splitPermission(p)[1] }));
    groups.push({ area, value: `${area}.all`, children });
  }
  return groups;
}

/**
 * Pannello admin per inviare una notifica a tutti gli admin, a uno specifico
 * publisher o a un singolo utente. Consegna in tempo reale via SSE (campanella +
 * toast lato ricevente) e, opzionalmente, via email. Gate: special_features.can_broadcast.
 */
@Component({
  selector: 'app-notify-broadcast-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './notify-broadcast-panel.component.html',
  styleUrl: './notify-broadcast-panel.component.scss'
})
export class NotifyBroadcastPanelComponent {
  @Output() close = new EventEmitter<void>();

  private http = inject(HttpClient);
  private notificationBackend = inject(NotificationBackendService);
  private notifications = inject(NotificationService);
  private translate = inject(TranslateService);
  private apiUrl = environment.apiUrl;

  targetType: NotificationTarget = 'admin';
  targetIds: string[] = [];
  message = '';
  messageType: NotificationStatus = 'info';
  sendEmail = false;
  sending = false;

  publishers: PublisherOption[] = [];
  users: UserOption[] = [];
  loadingOptions = false;
  search = '';

  // Permessi selezionati come filtro (valori dot-notation completi). OR tra loro.
  selectedPerms: string[] = [];
  permTreeOpen = false;
  readonly permFilterGroups: readonly PermFilterGroup[] = buildPermFilterGroups();

  readonly messageTypes: NotificationStatus[] = ['info', 'success', 'warning', 'error'];

  onTargetTypeChange(): void {
    this.targetIds = [];
    this.search = '';
    this.selectedPerms = [];
    this.permTreeOpen = false;
    if (this.targetType === 'entity' && !this.publishers.length) this.loadPublishers();
    if (this.targetType === 'user' && !this.users.length) this.loadUsers();
  }

  private loadPublishers(): void {
    this.loadingOptions = true;
    this.http.get<{ payload: PublisherOption[] }>(`${this.apiUrl}/webapp/crud/publishers`)
      .subscribe({
        next: r => { this.publishers = r.payload ?? []; this.loadingOptions = false; },
        error: () => { this.loadingOptions = false; }
      });
  }

  private loadUsers(): void {
    this.loadingOptions = true;
    this.http.get<{ payload: UserOption[] }>(`${this.apiUrl}/webapp/crud/users`)
      .subscribe({
        next: r => {
          this.users = (r.payload ?? []).map(u => ({ ...u, permissions: this.normalizePermissions(u.permissions) }));
          this.loadingOptions = false;
        },
        error: () => { this.loadingOptions = false; }
      });
  }

  // I permessi arrivano da JSON_EXTRACT lato backend: a seconda del driver mysql
  // possono essere già un array oppure una stringa JSON. Normalizziamo sempre a string[].
  private normalizePermissions(raw: unknown): string[] {
    if (Array.isArray(raw)) return raw.filter((p): p is string => typeof p === 'string');
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  // Aree e sotto-permessi in ordine alfabetico per etichetta tradotta (stabile
  // rispetto alla lingua). Il permesso globale 'all' resta fuori, fissato in cima.
  get sortedPermGroups(): PermFilterGroup[] {
    const byLabel = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    return [...this.permFilterGroups]
      .map(g => ({
        ...g,
        children: [...g.children].sort((a, b) =>
          byLabel(this.permLeafLabel(g.area, a.leaf), this.permLeafLabel(g.area, b.leaf)))
      }))
      .sort((a, b) => byLabel(
        this.translate.instant(`notifyBroadcast.permAreas.${a.area}`),
        this.translate.instant(`notifyBroadcast.permAreas.${b.area}`)));
  }

  trackByPermValue(_index: number, item: { value: string }): string {
    return item.value;
  }

  togglePerm(value: string): void {
    this.selectedPerms = this.selectedPerms.includes(value)
      ? this.selectedPerms.filter(p => p !== value)
      : [...this.selectedPerms, value];
  }

  removePerm(value: string): void {
    this.selectedPerms = this.selectedPerms.filter(p => p !== value);
  }

  isPermSelected(value: string): boolean {
    return this.selectedPerms.includes(value);
  }

  // Etichetta di una foglia dell'albero (ultimo segmento). 'all' → "All".
  permLeafLabel(area: string, leaf: string): string {
    if (leaf === 'all') return this.translate.instant('notifyBroadcast.permFilter.all');
    return this.translate.instant(`notifyBroadcast.permFilter.leaves.${area}.${leaf}`);
  }

  // Etichetta della chip per un permesso completo: "All", "Area" oppure "Area: Foglia".
  permChipLabel(value: string): string {
    const [area, leaf] = splitPermission(value);
    if (area === 'all') return this.translate.instant('notifyBroadcast.permFilter.all');
    const areaLabel = this.translate.instant(`notifyBroadcast.permAreas.${area}`);
    if (!leaf || leaf === 'all') return areaLabel;
    return `${areaLabel}: ${this.permLeafLabel(area, leaf)}`;
  }

  // Un utente matcha se soddisfa almeno uno dei permessi selezionati.
  // Match gerarchico: chi ha '<area>.all' o 'all' rientra nei filtri più specifici.
  private matchesPerms(u: UserOption): boolean {
    if (!this.selectedPerms.length) return true;
    return userHasAnyPermission(u.permissions, this.selectedPerms);
  }

  get filteredPublishers(): PublisherOption[] {
    const t = this.search.trim().toLowerCase();
    if (!t) return this.publishers;
    return this.publishers.filter(p =>
      (p.Name ?? '').toLowerCase().includes(t) || (p.ID ?? '').toLowerCase().includes(t));
  }

  get filteredUsers(): UserOption[] {
    const t = this.search.trim().toLowerCase();
    return this.users.filter(u => {
      if (!this.matchesPerms(u)) return false;
      if (!t) return true;
      return (u.email ?? '').toLowerCase().includes(t) || (u.ID ?? '').toLowerCase().includes(t);
    });
  }

  isSelected(id: string): boolean {
    return this.targetIds.includes(id);
  }

  toggleTarget(id: string): void {
    this.targetIds = this.isSelected(id)
      ? this.targetIds.filter(x => x !== id)
      : [...this.targetIds, id];
  }

  removeTarget(id: string): void {
    this.targetIds = this.targetIds.filter(x => x !== id);
  }

  labelFor(id: string): string {
    if (this.targetType === 'entity') {
      const p = this.publishers.find(x => x.ID === id);
      return p ? p.Name : id;
    }
    if (this.targetType === 'user') {
      const u = this.users.find(x => x.ID === id);
      return u ? u.email : id;
    }
    return id;
  }

  get canSend(): boolean {
    if (this.sending) return false;
    if (!this.message.trim()) return false;
    if (this.targetType !== 'admin' && this.targetIds.length === 0) return false;
    return true;
  }

  submit(): void {
    if (!this.canSend) return;
    this.sending = true;
    this.notificationBackend.sendNotification({
      TargetType: this.targetType,
      TargetIDs: this.targetType === 'admin' ? [] : this.targetIds,
      Message: this.message.trim(),
      MessageType: this.messageType,
      sendEmail: this.sendEmail
    }).subscribe({
      next: () => {
        this.sending = false;
        this.notifications.notify('success', this.translate.instant('notifyBroadcast.sent'));
        this.closeModal();
      },
      error: () => {
        this.sending = false;
        this.notifications.notify('error', this.translate.instant('notifyBroadcast.failed'));
      }
    });
  }

  closeModal(): void {
    this.close.emit();
  }
}
